import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { InteractiveStackedCards } from '@sorare/core/src/components/cards/InteractiveStackedCards';
import Dialog from '@sorare/core/src/components/dialog';
import { isTypeFn } from '@sorare/core/src/gql';
import { useIsMediumMobileHeightAndAbove } from '@sorare/core/src/hooks/device/useIsMediumMobileHeightAndAbove';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type {
  ClaimDailyMissionMutation,
  ClaimDailyMissionMutationVariables,
  ClaimDailyPackDialog_managerProgressionMission,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0 })`
  min-height: 100%;
`;
const Content = styled(Vertical).attrs({ center: true })`
  justify-content: space-between;
  padding: var(--double-unit) var(--quadruple-unit);
  gap: var(--double-unit);
  flex: 1;

  @media ${laptopAndAbove} {
    min-height: min(calc(100vh - var(--unit) * 12), 600px);
  }
`;
const ImgWrapper = styled(Vertical)`
  flex: 1;
`;
const Img = styled(CardImg)`
  width: 240px;
  transform: rotate(3deg);
`;
const Header = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 80px;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--double-unit);

  & > *:first-child {
    justify-self: flex-start;
  }
  & > *:last-child {
    justify-self: flex-end;
  }
`;
const StyledButton = styled(Button)`
  && {
    min-width: 200px;
  }
`;
const StyledLoadingButton = styled(LoadingButton)`
  && {
    min-width: 200px;
  }
`;

export const CLAIM_DAILY_MISSION_MUTATION = gql`
  mutation ClaimDailyMissionMutation(
    $name: ManagerProgressionTaskSlug!
    $sport: Sport!
  ) {
    claimManagerProgressionTask(input: { name: $name, sport: $sport }) {
      managerProgressionTaskOrMission {
        id
        aasmState
        cards {
          slug
          pictureUrl
        }
      }
      currentUser {
        slug
        managerProgression(sport: $sport) {
          id
          categories(names: COMMON_GAME) {
            id
            categoryTasks {
              ... on ManagerProgressionMission {
                id
                aasmState
                claimableAt
              }
              ... on ManagerProgressionTask {
                id
              }
            }
          }
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  ClaimDailyMissionMutation,
  ClaimDailyMissionMutationVariables
>;

type Props = {
  open: boolean;
  onClose: () => void;
  mission: Nullable<ClaimDailyPackDialog_managerProgressionMission>;
};
export const ClaimDailyPackDialog = ({ open, onClose, mission }: Props) => {
  const cardPackReward = mission?.configRewards?.find(
    isTypeFn('CardPackReward')
  );
  const cardPack = cardPackReward?.cardPack;
  const { pictureUrl } = cardPack || {};
  const [claimMission, { loading }] = useMutation(
    CLAIM_DAILY_MISSION_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );
  const isMediumMobileHeightAndAbove = useIsMediumMobileHeightAndAbove();
  const [cards, setCards] = useState<
    NonNullable<
      NonNullable<
        NonNullable<
          ClaimDailyMissionMutation['claimManagerProgressionTask']
        >['managerProgressionTaskOrMission']
      >['cards']
    >
  >([]);

  const onClaim = async () => {
    if (!mission) {
      return;
    }
    const { data } = await claimMission({
      variables: {
        name: mission.name,
        sport: Sport.FOOTBALL,
      },
    });
    setCards(
      data?.claimManagerProgressionTask?.managerProgressionTaskOrMission
        ?.cards || []
    );
  };

  return (
    <Dialog open={open} onClose={onClose} hideHeader fullWidth maxWidth="xs">
      {({ CloseButton }) => (
        <Root>
          <Header>
            <div />
            <HeadlineL>
              <FormattedMessage
                id="ClaimDailyPackDialog.title"
                defaultMessage="Daily Claim"
              />
            </HeadlineL>
            <CloseButton onClose={onClose} />
          </Header>
          <Content>
            {cards.length === 0 ? (
              <>
                <ImgWrapper center>
                  <Img src={pictureUrl} alt="" width={320} />
                </ImgWrapper>
                <StyledLoadingButton
                  color="primary"
                  size="medium"
                  loading={loading}
                  onClick={onClaim}
                >
                  <FormattedMessage {...glossary.claim} />
                </StyledLoadingButton>
              </>
            ) : (
              <>
                <ImgWrapper center>
                  <InteractiveStackedCards
                    restack
                    cards={cards.map(card => card.pictureUrl).filter(Boolean)}
                    cardWidth={isMediumMobileHeightAndAbove ? 240 : 180}
                  />
                </ImgWrapper>
                <StyledButton onClick={onClose} size="medium">
                  <FormattedMessage {...glossary.continue} />
                </StyledButton>
              </>
            )}
          </Content>
        </Root>
      )}
    </Dialog>
  );
};

ClaimDailyPackDialog.fragments = {
  managerProgressionMission: gql`
    fragment ClaimDailyPackDialog_managerProgressionMission on ManagerProgressionMission {
      id
      name
      aasmState
      configRewards {
        ... on CardPackReward {
          cardPack {
            slug
            pictureUrl
          }
        }
      }
    }
  ` as TypedDocumentNode<ClaimDailyPackDialog_managerProgressionMission>,
};
